/**
 * The invite sheet's `People` tab body: a search field and a phase-driven
 * result list. Deliberately data-source-free, so it is fully testable
 * against plain props before ISSUE-189's `/invite/user-search` is wired up
 * in WI-9.
 */
import { Input, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { InviteResultRow } from "./InviteResultRow";
import {
  buildInviteResultRows,
  type typeInviteSearchHit,
} from "./inviteResultRows";
import {
  INVITE_SEARCH_LABEL,
  INVITE_TAB_NOT_READY_NOTE,
  PEOPLE_NO_RESULTS_MESSAGE,
  PEOPLE_SEARCHING_LABEL,
  PEOPLE_START_MESSAGE,
} from "./inviteSheetCopy";

export type typeInvitePeopleTabPhase =
  "notReady" | "idle" | "searching" | "ready";

export type typeInvitePeopleTabProps = {
  phase: typeInvitePeopleTabPhase;
  query: string;
  onQueryChange: (next: string) => void;
  hits: readonly typeInviteSearchHit[];
  locallyInvited: ReadonlySet<string>;
  busyUserId: string | null;
  onInvite: (userId: string) => void;
};

export const InvitePeopleTab = ({
  phase,
  query,
  onQueryChange,
  hits,
  locallyInvited,
  busyUserId,
  onInvite,
}: typeInvitePeopleTabProps): ReactNode => {
  const rows =
    phase === "ready" ? buildInviteResultRows({ hits, locallyInvited }) : [];

  return (
    <div className="flex flex-col gap-3">
      <Input
        label={INVITE_SEARCH_LABEL}
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
        }}
        data-testid="invite-people-search"
      />
      {phase === "notReady" ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="invite-people-not-ready"
        >
          {INVITE_TAB_NOT_READY_NOTE}
        </p>
      ) : null}
      {phase === "idle" ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="invite-people-start"
        >
          {PEOPLE_START_MESSAGE}
        </p>
      ) : null}
      {phase === "searching" ? (
        <div data-testid="invite-people-searching">
          <Skeleton shape="control" label={PEOPLE_SEARCHING_LABEL} />
        </div>
      ) : null}
      {phase === "ready" && rows.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="invite-people-empty"
        >
          {PEOPLE_NO_RESULTS_MESSAGE}
        </p>
      ) : null}
      {phase === "ready" && rows.length > 0
        ? rows.map((row) => (
            <InviteResultRow
              key={row.userId}
              row={row}
              isBusy={busyUserId === row.userId}
              onInvite={onInvite}
            />
          ))
        : null}
    </div>
  );
};
